import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Users2, Plus, Search, Hash, Lock } from 'lucide-react';

interface ChatGroup {
  id: string;
  name: string;
  description: string;
  members: number;
  type: 'public' | 'private';
  lastActivity: string;
}

const mockGroups: ChatGroup[] = [
  { id: '1', name: 'Support Team', description: 'General support team channel', members: 12, type: 'public', lastActivity: '5 min ago' },
  { id: '2', name: 'IT Helpdesk', description: 'IT related issues and resolutions', members: 8, type: 'public', lastActivity: '1 hr ago' },
  { id: '3', name: 'Admin Only', description: 'Admin discussions', members: 3, type: 'private', lastActivity: '2 hrs ago' },
  { id: '4', name: 'Field Agents', description: 'On-ground agent coordination', members: 15, type: 'public', lastActivity: 'Yesterday' },
];

export function AdminChatGroups() {
  const [search, setSearch] = useState('');

  const filtered = mockGroups.filter(g =>
    g.name.toLowerCase().includes(search.toLowerCase()) ||
    g.description.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="flex h-full flex-col gap-4 p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Users2 className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-foreground">Groups</h1>
            <p className="text-sm text-muted-foreground">Manage chat groups and channels</p>
          </div>
        </div>
        <Button className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Create Group
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search groups..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {filtered.map(group => (
          <Card key={group.id} className="cursor-pointer transition-shadow hover:shadow-md">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {group.type === 'private' ? (
                    <Lock className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Hash className="h-4 w-4 text-primary" />
                  )}
                  <CardTitle className="text-base">{group.name}</CardTitle>
                </div>
                <Badge variant={group.type === 'private' ? 'secondary' : 'outline'}>
                  {group.type}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <p className="mb-3 text-sm text-muted-foreground">{group.description}</p>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Users2 className="h-3 w-3" />
                  {group.members} members
                </span>
                <span>{group.lastActivity}</span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="flex flex-1 items-center justify-center text-muted-foreground">
          No groups found
        </div>
      )}
    </div>
  );
}
